/**
 * Xodim profili (talab 2026-10-10): kim, natijalar (tanlangan oy va oxirgi 6 oy), driverlari, maoshi, faolligi.
 * Ko'rinish va maosh ruxsati backend'da (apps/dashboards/people.py); maosh tabi faqat ruxsat bo'lsa.
 */
import { usePageSize } from "@/lib/pageSize";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AtSign, BadgeCheck, Ban, Check, KeyRound, Mail, MessageSquare, MessageSquareText, Pencil, Phone, RotateCcw, Trophy, UserCheck, Users, Wallet } from "lucide-react";
import { ApiError } from "@/lib/api";
import { absoluteUrl, callPhone, copyText } from "@/lib/clipboard";
import { cn, dayjs, formatDate, formatMoney, formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { useUnblockUser } from "@/features/admin/api";
import { payError, useAdjustBonus, useApproveBonuses, useMarkBonusesPaid, useMarkSalariesPaid, useUpsertSalaries } from "@/features/budget/api";
import { money } from "@/features/budget/types";
import { currentPeriod } from "@/features/dashboards/api";
import { useDrivers } from "@/features/drivers/api";
import type { DriverListItem, DriverStatus } from "@/features/drivers/types";
import { usePersonProfile, type PersonActivity, type PersonProfile } from "@/features/people/api";
import { personMenu } from "@/features/people/menu";
import { Badge, StatusPill, STATUS_BG } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, KpiCard } from "@/components/ui/card";
import { ContextMenu } from "@/components/ui/context-menu";
import { Input, NumberInput } from "@/components/ui/field";
import { Avatar, EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { Tabs } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { BlockModal, UserFormModal } from "@/pages/admin/UsersPage";
import { AccessModal } from "@/pages/admin/AccessModal";
import { hasAccessSettings } from "@/features/admin/menu";
import { PayModal, type PayResult } from "@/features/finance/PayModal";
import { FunnelBars, PeriodSelect, RetentionBars, days, pct } from "@/features/dashboards/blocks";
import { useDriverActions } from "@/pages/drivers/useDriverActions";
import { can } from "@/features/auth/types";

const PIPELINE: DriverStatus[] = ["new", "contacted", "qualified", "docs", "submitted", "background_check", "orientation"];
const BAR_PRIMARY = "var(--color-chart)";
const BAR_SOFT = "var(--color-chart-soft)";

export function PersonPage() {
  const { t } = useTranslation("people");
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const period = params.get("period") ?? currentPeriod();
  const profile = usePersonProfile(id, period);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  if (profile.isPending) return <PageLoading />;
  if (profile.isError) {
    if (profile.error instanceof ApiError && (profile.error.status === 404 || profile.error.status === 403)) {
      return <EmptyState title={t("noAccess")} description={t("noAccessDesc")} action={<Link to="/" className="text-primary hover:underline">{t("common:states.backHome")}</Link>} />;
    }
    return <ErrorState message={profile.error.message} onRetry={() => profile.refetch()} />;
  }
  return <Person p={profile.data} period={period} tab={params.get("tab") ?? "overview"} setParam={setParam} />;
}

function Person({ p, period, tab, setParam }: { p: PersonProfile; period: string; tab: string; setParam: (k: string, v: string | null) => void }) {
  const { t } = useTranslation("people");
  const { t: ti } = useTranslation("metrics");
  const { t: tc } = useTranslation();
  const { t: tm } = useTranslation("menu");
  const me = useMe().data;
  const navigate = useNavigate();
  const unblock = useUnblockUser();
  const [editOpen, setEditOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const u = p.user;
  const accessSettings = !!me && !!p.admin_user && hasAccessSettings(u, me);
  const k = p.kpis;
  const month = dayjs(`${period}-01`).format("MMMM YYYY");

  async function doUnblock() {
    try {
      await unblock.mutateAsync(u.id);
      toast.success(t("unblocked", { name: u.display_name }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : tc("toast.failed"));
    }
  }

  const headerMenu = () =>
    personMenu(
      p,
      {
        navigate: (path) => navigate(path),
        copy: (text) => void copyText(text),
        copyLink: () => void copyText(absoluteUrl(`/people/${u.id}`)),
        call: callPhone,
        edit: () => setEditOpen(true),
        block: () => setBlockOpen(true),
        unblock: () => void doUnblock(),
        can: (perm) => can(me, perm),
        access: accessSettings ? () => setAccessOpen(true) : undefined,
      },
      tm,
    );

  const tabs = [
    { key: "overview", label: t("tabs.overview") },
    { key: "drivers", label: t("tabs.drivers", { count: u.drivers.total }) },
    ...(p.permissions.can_see_pay ? [{ key: "pay", label: t("tabs.pay") }] : []),
    { key: "activity", label: t("tabs.activity") },
  ];
  const activeTab = tabs.some((x) => x.key === tab) ? tab : "overview";

  return (
    <div>
      <div className="mb-2 text-[12px] text-muted">
        {can(me, "users") ? (
          <Link to="/admin/users" className="hover:underline">
            {t("breadcrumb")}
          </Link>
        ) : (
          t("breadcrumb")
        )}{" "}
        / <span className="font-semibold text-ink">{u.full_name}</span>
      </div>

      <ContextMenu items={headerMenu}>
        <Card className="px-5 py-4">
          <div className="flex items-start gap-4">
            <Avatar initials={u.initials} name={u.full_name} size={56} className="text-[18px]" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em]">{u.full_name}</h1>
                <Badge tone="blue">{tc(`roles.${u.role}`)}</Badge>
                {u.is_blocked && <Badge tone="red">{t("blocked")}</Badge>}
                {p.permissions.is_self && <Badge tone="dark">{t("you")}</Badge>}
              </div>
              <div className="mt-1 text-[13px] text-ink-soft">
                {u.branch?.name ?? t("noBranch")}
                {u.manager && (
                  <>
                    {" · "}
                    {t("manager")}{" "}
                    <Link to={`/people/${u.manager.id}`} className="font-semibold text-ink hover:underline">
                      {u.manager.display_name}
                    </Link>
                  </>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[13px] text-ink-soft">
                <span className="inline-flex items-center gap-1" title={t("username")}>
                  <AtSign size={13} /> {u.username}
                </span>
                {u.phone && (
                  <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <Phone size={13} /> {u.phone}
                  </a>
                )}
              </div>
              <div className="mt-0.5 text-[12px] text-muted">
                {t("since", { date: formatDate(u.created_at) })} · {u.last_login ? t("lastLogin", { when: formatWhen(u.last_login) }) : t("neverLoggedIn")}
                {u.totp_enabled ? ` · ${t("twofaOn")}` : ""}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              {u.drivers.total > 0 && (
                <Button onClick={() => navigate(`/drivers?owner=${u.id}`)}>
                  <Users size={14} /> {t("showDrivers")}
                </Button>
              )}
              {p.permissions.can_manage && p.admin_user && (
                <Button onClick={() => setEditOpen(true)}>
                  <Pencil size={14} /> {tc("actions.edit")}
                </Button>
              )}
              {accessSettings && (
                <Button onClick={() => setAccessOpen(true)}>
                  <KeyRound size={14} /> {t("admin:access.open")}
                </Button>
              )}
              {p.permissions.can_manage && p.admin_user && !p.permissions.is_self &&
                (u.is_blocked ? (
                  <Button onClick={doUnblock} loading={unblock.isPending}>
                    <UserCheck size={14} /> {t("unblock")}
                  </Button>
                ) : (
                  <Button variant="danger" onClick={() => setBlockOpen(true)}>
                    <Ban size={14} /> {t("block")}
                  </Button>
                ))}
            </div>
          </div>
        </Card>
      </ContextMenu>

      <div className="mt-5 mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[16px] font-bold">{t("results", { month })}</h2>
        <PeriodSelect value={period} onChange={(v) => setParam("period", v === currentPeriod() ? null : v)} />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard to={`/drivers?owner=${p.user.id}&hired_month=${period}`} info={ti("hires")} label={t("kpi.hires")} value={k.hires} sub={t("kpi.prev", { count: k.hires_prev })} subTone={k.hires >= k.hires_prev ? "success" : "danger"} />
        <KpiCard to={`/drivers?owner=${p.user.id}&submitted_month=${period}`} info={ti("submitted")} label={t("kpi.submitted")} value={k.submitted} sub={t("kpi.waitingReply", { count: k.waiting_reply })} />
        <KpiCard to={`/drivers?owner=${p.user.id}&added_month=${period}`} info={ti("newDrivers")} label={t("kpi.newDrivers")} value={k.new_drivers} sub={t("kpi.prev", { count: k.new_drivers_prev })} />
        <KpiCard info={ti("calls")} label={t("kpi.calls")} value={k.calls} sub={t("kpi.activities", { count: k.activities })} />
        <KpiCard to={`/pipeline?owner=${p.user.id}`} info={ti("inPipeline")} label={t("kpi.inPipeline")} value={k.in_pipeline} sub={k.hires_pending ? t("kpi.pendingHires", { count: k.hires_pending }) : undefined} />
        <KpiCard info={ti("avgTime")} label={t("kpi.timeToHire")} value={days(k.avg_time_to_hire_days)} sub={k.avg_time_to_hire_prev !== null ? t("kpi.prevValue", { value: days(k.avg_time_to_hire_prev) }) : undefined} />
        <KpiCard info={ti("retention30")} label={t("kpi.retention30")} value={pct(k.retention_30)} />
        <KpiCard to={`/tasks?assignee=${p.user.id}`} info={ti("overdueTasks")} label={t("kpi.overdue")} value={k.overdue_tasks} subTone={k.overdue_tasks > 0 ? "danger" : "muted"} sub={k.overdue_tasks > 0 ? t("kpi.overdueSub") : t("kpi.noOverdue")} />
      </div>

      <Tabs items={tabs} value={activeTab} onChange={(key) => setParam("tab", key === "overview" ? null : key)} className="mt-5 mb-4" />

      {activeTab === "overview" && <Overview p={p} />}
      {activeTab === "drivers" && <PersonDrivers personId={u.id} total={u.drivers.total} />}
      {activeTab === "pay" && p.pay && <Pay p={p} period={period} />}
      {activeTab === "activity" && <ActivityList items={p.recent_activity} />}

      {p.admin_user && me && (
        <>
          <UserFormModal open={editOpen} onOpenChange={setEditOpen} me={me} user={p.admin_user} />
          <BlockModal user={blockOpen ? p.admin_user : null} onOpenChange={(o) => !o && setBlockOpen(false)} />
          <AccessModal user={accessOpen ? p.admin_user : null} onOpenChange={setAccessOpen} />
        </>
      )}
    </div>
  );
}

function monthLabel(period: string): string {
  return dayjs(`${period}-01`).format("MMM");
}

function Overview({ p }: { p: PersonProfile }) {
  const { t } = useTranslation("people");
  const { t: tc } = useTranslation();
  const chart = p.monthly.map((m) => ({ name: monthLabel(m.period), hires: m.hires, submitted: m.submitted, calls: m.calls }));
  const pipelineMax = Math.max(1, ...PIPELINE.map((s) => p.pipeline[s] ?? 0));
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card>
          <CardHeader title={t("trend.title")} subtitle={t("trend.subtitle")} />
          <CardBody>
            <div className="h-[240px] w-full">
              <ResponsiveContainer>
                <BarChart data={chart} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--color-grid)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--color-muted)" }} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--color-muted)" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "var(--color-sidebar)" }} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-ink)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-ink-soft">{v}</span>} />
                  <Bar dataKey="submitted" name={t("trend.submitted")} fill={BAR_SOFT} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="hires" name={t("trend.hires")} fill={BAR_PRIMARY} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t("funnel.title")} subtitle={t("funnel.subtitle")} />
          <CardBody>
            <FunnelBars steps={p.funnel} />
          </CardBody>
        </Card>
      </div>
      <div className="space-y-4">
        {p.rank?.position && (
          <Card>
            <CardBody className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-budget-soft text-budget">
                <Trophy size={18} />
              </span>
              <div>
                <div className="text-[20px] leading-tight font-bold">{p.rank.position ? `#${p.rank.position}` : "—"}</div>
                <div className="text-[12px] text-muted">{t("rank", { of: p.rank.of, branch: p.rank.branch })}</div>
              </div>
            </CardBody>
          </Card>
        )}
        {p.pay?.estimate && (
          <Card>
            <CardHeader title={t("bonus.title")} subtitle={p.pay.estimate.rule} />
            <CardBody>
              <div className="text-[24px] font-bold">{formatMoney(p.pay.estimate.amount, p.pay.estimate.currency)}</div>
              <div className="mt-1 text-[12px] text-muted">
                {t("bonus.basis", { hires: p.pay.estimate.hires, retained: p.pay.estimate.retained_30 })} · {tc(`status.${p.pay.estimate.status}`, { defaultValue: t("bonus.estimate") })}
              </div>
            </CardBody>
          </Card>
        )}
        <Card>
          <CardHeader title={t("pipeline.title")} subtitle={t("pipeline.subtitle", { count: PIPELINE.reduce((n, s) => n + (p.pipeline[s] ?? 0), 0) })} />
          <CardBody className="space-y-1.5">
            {PIPELINE.map((s) => (
              <div key={s} className="grid grid-cols-[120px_1fr_32px] items-center gap-2 text-[12px]">
                <span className="truncate text-ink-soft">{tc(`status.${s}`)}</span>
                <div className="h-4 rounded-sm bg-fill">
                  <div className={cn("h-4 rounded-sm", STATUS_BG[s])} style={{ width: `${((p.pipeline[s] ?? 0) * 100) / pipelineMax}%` }} />
                </div>
                <span className="text-right font-semibold">{p.pipeline[s] ?? 0}</span>
              </div>
            ))}
          </CardBody>
        </Card>
        <Card>
        <CardHeader title={t("retention.title")} subtitle={t("retention.subtitle")} />
        <CardBody>
          <RetentionBars rows={p.retention} />
        </CardBody>
      </Card>
      </div>
    </div>
  );
}

function PersonDrivers({ personId, total }: { personId: number; total: number }) {
  const { t } = useTranslation("people");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize("person-drivers");
  const drivers = useDrivers({ owner: String(personId), page, page_size: pageSize, ordering: "-status_changed_at" });
  const driverActions = useDriverActions();
  if (drivers.isPending) return <PageLoading />;
  if (drivers.isError) return <ErrorState message={drivers.error.message} onRetry={() => drivers.refetch()} />;
  const rows: DriverListItem[] = drivers.data.results;
  if (rows.length === 0) return <EmptyState title={t("drivers.empty")} description={t("drivers.emptyDesc")} />;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-[12px] text-muted">
        <span>{t("drivers.hint", { count: total })}</span>
        <Link to={`/drivers?owner=${personId}`} className="text-primary hover:underline">
          {t("drivers.openList")}
        </Link>
      </div>
      <Table>
        <thead>
          <tr>
            <Th>{t("drivers.cols.name")}</Th>
            <Th>{t("drivers.cols.phone")}</Th>
            <Th>{t("drivers.cols.state")}</Th>
            <Th>{t("drivers.cols.status")}</Th>
            <Th>{t("drivers.cols.lastContact")}</Th>
            <Th>{t("drivers.cols.nextAction")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <ContextMenu key={d.id} items={() => driverActions.items(d)}>
              <Tr clickable onClick={() => navigate(`/drivers/${d.id}`)}>
                <Td className="font-semibold">{d.full_name}</Td>
                <Td className="text-ink-soft">{d.phone_display}</Td>
                <Td>{d.state || "—"}</Td>
                <Td>
                  <StatusPill status={d.status} />
                </Td>
                <Td className="text-ink-soft">{formatWhen(d.last_contact_at)}</Td>
                <Td className={cn("text-[12px]", d.next_reminder?.overdue ? "font-semibold text-danger" : "text-ink-soft")}>
                  {d.next_reminder ? `${tc(`reminder.${d.next_reminder.kind}`)} · ${formatWhen(d.next_reminder.due_at)}` : "—"}
                </Td>
              </Tr>
            </ContextMenu>
          ))}
        </tbody>
      </Table>
      <Pagination page={drivers.data.page} pages={drivers.data.pages} count={drivers.data.count} pageSize={drivers.data.page_size}
        onPage={setPage}
        onPageSize={(s) => {
          setPageSize(s);
          setPage(1);
        }}
      />
      {driverActions.dialogs}
    </div>
  );
}

/**
 * Tanlangan oy: maosh va bonus, kerak bo'lsa shu yerning o'zida boshqarish. Budget bilan bir xil API va qoida
 * (backend can_manage_pay); oy sahifa tepasidagi tanlovdan olinadi. Bonus faqat recruiterlarda bo'ladi.
 */
function PayMonth({ p, period }: { p: PersonProfile; period: string }) {
  const { t } = useTranslation("people");
  const month = dayjs(`${period}-01`).format("MMMM YYYY");
  const recruiter = p.user.role === "recruiter";
  return (
    <Card className="lg:col-span-3">
      <CardHeader title={t("pay.monthTitle", { month })} subtitle={t("pay.monthHint")} />
      <CardBody>
        <div className={cn("grid grid-cols-1 gap-4", recruiter && "md:grid-cols-2")}>
          <SalaryBox p={p} period={period} />
          {recruiter && <BonusBox p={p} period={period} />}
        </div>
      </CardBody>
    </Card>
  );
}

function SalaryBox({ p, period }: { p: PersonProfile; period: string }) {
  const { t } = useTranslation("people");
  const { t: tc } = useTranslation();
  const me = useMe().data;
  const upsert = useUpsertSalaries();
  const markPaid = useMarkSalariesPaid();
  const { t: tb } = useTranslation("budget");
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const { salary: s, suggested } = p.pay!.month;
  const canEdit = p.permissions.can_edit_pay;
  const name = p.user.display_name;

  function startEdit() {
    setAmount(s ? String(money(s.amount)) : suggested !== null ? String(money(suggested)) : "");
    setNote(s?.note ?? "");
    setEditing(true);
  }

  async function save() {
    try {
      await upsert.mutateAsync({ period, items: [{ user_id: p.user.id, amount, note, version: s?.version ?? null }] });
      toast.success(t("pay.salarySaved", { name }));
      setEditing(false);
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
      if (err instanceof ApiError && err.isConflict) setEditing(false);
    }
  }

  async function setPaid(paid: boolean, result?: PayResult) {
    if (!s) return;
    // To'landi -- avval qaysi hisobdan (oyna), keyin saqlash
    if (paid && !result) {
      setPaying(true);
      return;
    }
    try {
      await markPaid.mutateAsync({ rows: [s], paid, ...result });
      toast.success(paid ? t("pay.markedPaid", { name }) : t("pay.markedUnpaid", { name }));
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
    setPaying(false);
  }

  return (
    <section className="rounded-2xl border border-line p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-ink-soft">{t("pay.salary")}</h3>
        {s ? <Badge tone={s.paid ? "green" : "orange"}>{s.paid ? (s.paid_at ? t("pay.paidOn", { date: formatDate(s.paid_at) }) : t("pay.paid")) : t("pay.unpaid")}</Badge> : <Badge>{t("pay.notSet")}</Badge>}
      </div>
      {editing ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <NumberInput min={0} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" aria-label={t("pay.amount")} autoFocus />
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder={t("pay.notePlaceholder")} aria-label={t("pay.note")} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" variant="primary" loading={upsert.isPending} disabled={amount === ""}>
              {tc("actions.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {tc("actions.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className={cn("mt-2 text-[26px] leading-tight font-bold", !s && "text-muted")}>{s ? formatMoney(money(s.amount), s.currency) : "—"}</div>
          <div className="mt-0.5 min-h-[18px] text-[12px] text-muted">
            {s ? s.note : suggested !== null ? t("pay.lastMonth", { amount: formatMoney(money(suggested)) }) : null}
          </div>
          {canEdit && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {!s?.paid && (
                <Button size="sm" onClick={startEdit}>
                  <Pencil size={13} /> {s ? t("pay.changeAmount") : t("pay.setSalary")}
                </Button>
              )}
              {s && !s.paid && (
                <Button size="sm" variant="primary" loading={markPaid.isPending} onClick={() => void setPaid(true)}>
                  <Check size={13} /> {t("pay.markPaid")}
                </Button>
              )}
              {s?.paid &&
                (me?.role === "owner" ? (
                  <Button size="sm" loading={markPaid.isPending} onClick={() => void setPaid(false)}>
                    <RotateCcw size={13} /> {t("pay.markUnpaid")}
                  </Button>
                ) : (
                  <span className="text-[12px] text-muted">{t("pay.paidLocked")}</span>
                ))}
            </div>
          )}
        </>
      )}
      {s && (
        <PayModal
          open={paying}
          onOpenChange={setPaying}
          title={tb("pay.salaryTitle", { count: 1 })}
          description={name}
          amounts={[{ amount: money(s.amount), currency: s.currency }]}
          branch={p.user.branch?.id}
          allowNoAccount
          confirmLabel={tb("pay.markPaid")}
          loading={markPaid.isPending}
          onConfirm={(r) => setPaid(true, r)}
        />
      )}
    </section>
  );
}

function BonusBox({ p, period }: { p: PersonProfile; period: string }) {
  const { t } = useTranslation("people");
  const { t: tc } = useTranslation();
  const adjust = useAdjustBonus();
  const approve = useApproveBonuses();
  const markPaid = useMarkBonusesPaid();
  const { t: tb } = useTranslation("budget");
  const [paying, setPaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const b = p.pay!.month.bonus;
  const estimate = p.pay!.estimate;
  const canEdit = p.permissions.can_edit_pay;
  const name = p.user.display_name;

  async function run(action: () => Promise<unknown>, done: string) {
    try {
      await action();
      toast.success(done);
      setEditing(false);
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
      if (err instanceof ApiError && err.isConflict) setEditing(false);
    }
  }

  const header = (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-[13px] font-semibold text-ink-soft">{t("pay.bonus")}</h3>
      {b ? <StatusPill status={b.status} /> : <Badge>{t("pay.bonusNone")}</Badge>}
    </div>
  );

  if (!b) {
    return (
      <section className="rounded-2xl border border-line p-4">
        {header}
        <div className="mt-2 text-[26px] leading-tight font-bold text-muted">—</div>
        <div className="mt-0.5 min-h-[18px] text-[12px] text-muted">
          {estimate && estimate.hires > 0 ? t("pay.bonusEstimate", { amount: formatMoney(estimate.amount, estimate.currency), count: estimate.hires }) : null}
        </div>
        {canEdit && (
          <Link to={`/budget/bonuses?period=${period}`} className="mt-3 inline-block text-[13px] text-primary hover:underline">
            {t("pay.calculateInBudget")}
          </Link>
        )}
      </section>
    );
  }

  const adj = money(b.adjustment);
  return (
    <section className="rounded-2xl border border-line p-4">
      {header}
      {editing ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => adjust.mutateAsync({ id: b.id, adjustment: amount || "0", adjustment_note: note, version: b.version }), t("pay.bonusAdjusted", { name }));
          }}
        >
          <div className="text-[12px] text-ink-soft">{t("pay.calculated", { amount: formatMoney(money(b.calculated_amount), b.currency) })}</div>
          <NumberInput step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" aria-label={t("pay.adjustment")} autoFocus />
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} placeholder={t("pay.adjustmentReason")} aria-label={t("pay.adjustmentReason")} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" variant="primary" loading={adjust.isPending}>
              {tc("actions.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {tc("actions.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="mt-2 text-[26px] leading-tight font-bold">{formatMoney(money(b.total), b.currency)}</div>
          <div className="mt-0.5 text-[12px] text-muted">
            {t("bonus.basis", { hires: b.hires, retained: b.retained_30 })} · {b.rule_name}
          </div>
          {adj !== 0 && (
            <div className="text-[12px] text-ink-soft">
              {t("pay.calcLine", { calculated: formatMoney(money(b.calculated_amount), b.currency), adjustment: `${adj > 0 ? "+" : "−"}${formatMoney(Math.abs(adj), b.currency)}` })}
              {b.adjustment_note ? ` · ${b.adjustment_note}` : ""}
            </div>
          )}
          {canEdit && b.status !== "paid" && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {b.status === "draft" && (
                <Button
                  size="sm"
                  onClick={() => {
                    setAmount(String(adj));
                    setNote(b.adjustment_note);
                    setEditing(true);
                  }}
                >
                  <Pencil size={13} /> {t("pay.adjust")}
                </Button>
              )}
              {b.status === "draft" && money(b.total) > 0 && (
                <Button size="sm" variant="primary" loading={approve.isPending} onClick={() => void run(() => approve.mutateAsync([b]), t("pay.bonusApproved", { name }))}>
                  <BadgeCheck size={13} /> {t("pay.approve")}
                </Button>
              )}
              {b.status === "approved" && (
                <Button size="sm" variant="primary" loading={markPaid.isPending} onClick={() => setPaying(true)}>
                  <Wallet size={13} /> {t("pay.markPaid")}
                </Button>
              )}
            </div>
          )}
        </>
      )}
      {b && (
        <PayModal
          open={paying}
          onOpenChange={setPaying}
          title={tb("pay.bonusTitle", { count: 1 })}
          description={name}
          amounts={[{ amount: money(b.total), currency: b.currency }]}
          branch={p.user.branch?.id}
          allowNoAccount
          confirmLabel={tb("pay.markPaid")}
          loading={markPaid.isPending}
          onConfirm={async (r) => {
            await run(() => markPaid.mutateAsync({ rows: [b], ...r }), t("pay.bonusPaid", { name }));
            setPaying(false);
          }}
        />
      )}
    </section>
  );
}

function Pay({ p, period }: { p: PersonProfile; period: string }) {
  const { t } = useTranslation("people");
  const { t: ti } = useTranslation("metrics");
  const { t: tc } = useTranslation();
  const pay = p.pay;
  if (!pay) return null;
  const currency = pay.salaries[0]?.currency ?? pay.bonuses[0]?.currency ?? "USD";
  const chart = p.monthly.map((m) => ({ name: monthLabel(m.period), salary: Number(m.salary ?? 0), bonus: Number(m.bonus ?? 0) }));
  // Faqat haqiqatan to'langanlar; to'lanmagani alohida ko'rsatiladi
  const paidTotal = pay.salaries.filter((x) => x.paid).reduce((n, x) => n + Number(x.amount), 0) + pay.bonuses.filter((b) => b.status === "paid").reduce((n, b) => n + Number(b.total), 0);
  const unpaidTotal = pay.salaries.filter((x) => !x.paid).reduce((n, x) => n + Number(x.amount), 0) + pay.bonuses.filter((b) => b.status !== "paid").reduce((n, b) => n + Number(b.total), 0);
  const ym = (y: number, m: number) => dayjs(`${y}-${String(m).padStart(2, "0")}-01`).format("MMMM YYYY");
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
      <PayMonth p={p} period={period} />
      <Card className="lg:col-span-2">
        <CardHeader title={t("pay.chartTitle")} subtitle={t("pay.chartSubtitle")} />
        <CardBody>
          <div className="h-[220px] w-full">
            <ResponsiveContainer>
              <BarChart data={chart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--color-grid)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--color-muted)" }} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--color-muted)" }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatMoney(v, currency)} width={70} />
                <Tooltip formatter={(v) => formatMoney(Number(v), currency)} cursor={{ fill: "var(--color-sidebar)" }} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-ink)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-ink-soft">{v}</span>} />
                <Bar dataKey="salary" stackId="pay" name={t("pay.salary")} fill={BAR_SOFT} />
                <Bar dataKey="bonus" stackId="pay" name={t("pay.bonus")} fill="var(--color-budget)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>
      <div className="self-start">
        <KpiCard info={ti("pay.total12")} label={t("pay.total12")} value={formatMoney(paidTotal, currency)} sub={unpaidTotal > 0 ? t("pay.unpaidSub", { amount: formatMoney(unpaidTotal, currency) }) : t("pay.total12Sub")} accent="budget" />
      </div>

      <Card className="lg:col-span-2">
        <CardHeader title={t("pay.salaries")} />
        <CardBody>
          {pay.salaries.length === 0 ? (
            <p className="text-[13px] text-muted">{t("pay.noSalaries")}</p>
          ) : (
            <Table variant="bare">
              <thead>
                <tr>
                  <Th>{t("pay.cols.month")}</Th>
                  <Th className="text-right">{t("pay.cols.amount")}</Th>
                  <Th>{t("pay.cols.status")}</Th>
                  <Th>{t("pay.cols.paidOn")}</Th>
                </tr>
              </thead>
              <tbody>
                {pay.salaries.map((s) => (
                  <Tr key={`${s.year}-${s.month}`}>
                    <Td>{ym(s.year, s.month)}</Td>
                    <Td className="text-right font-semibold">{formatMoney(s.amount, s.currency)}</Td>
                    <Td>
                      <Badge tone={s.paid ? "green" : "orange"}>{s.paid ? t("pay.paid") : t("pay.unpaid")}</Badge>
                    </Td>
                    <Td className="text-ink-soft">{formatDate(s.paid_at)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("pay.bonuses")} />
        <CardBody>
          {pay.bonuses.length === 0 ? (
            <p className="text-[13px] text-muted">{t("pay.noBonuses")}</p>
          ) : (
            <div className="space-y-2">
              {pay.bonuses.map((b) => (
                <div key={`${b.year}-${b.month}-${b.part}`} className="flex items-center justify-between gap-2 rounded-[10px] border border-line px-3 py-2 text-[13px]">
                  <div>
                    <div className="font-semibold">
                      {ym(b.year, b.month)}
                      {b.part > 1 && <span className="ml-1.5 text-[11px] font-medium text-primary">{t("bonus.supplement")}</span>}
                    </div>
                    <div className="text-[11px] text-muted">{t("bonus.basis", { hires: b.hires, retained: b.retained_30 })}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{formatMoney(b.total, b.currency)}</div>
                    <StatusPill status={b.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-[11px] text-muted">{tc("status.draft")} → {tc("status.approved")} → {tc("status.paid")}</p>
        </CardBody>
      </Card>
    </div>
  );
}

const ACTIVITY_ICON = { call: Phone, note: MessageSquareText, sms: MessageSquare, email: Mail, status: MessageSquareText } as const;

function ActivityList({ items }: { items: PersonActivity[] }) {
  const { t } = useTranslation("people");
  const { t: td } = useTranslation("drivers");
  if (items.length === 0) return <EmptyState title={t("activity.empty")} description={t("activity.emptyDesc")} />;
  return (
    <Card>
      <CardHeader title={t("activity.title")} subtitle={t("activity.subtitle")} />
      <CardBody>
        <ul className="divide-y divide-line">
          {items.map((a) => {
            const Icon = ACTIVITY_ICON[a.type] ?? MessageSquareText;
            const mins = a.duration_sec ? Math.max(1, Math.round(a.duration_sec / 60)) : null;
            return (
              <li key={a.id} className="flex gap-3 py-2.5">
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-fill text-ink-soft">
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-muted">
                    <Link to={`/drivers/${a.driver.id}`} className="font-semibold text-ink hover:underline">
                      {a.driver.full_name}
                    </Link>
                    {" · "}
                    {td(`profile.activity.types.${a.type}`, { defaultValue: a.type })}
                    {a.type === "call" && a.outcome ? ` · ${td(`profile.activity.outcomes.${a.outcome}`, { defaultValue: a.outcome })}` : ""}
                    {mins ? ` · ${t("activity.minutes", { count: mins })}` : ""}
                  </div>
                  {a.body && <div className="mt-0.5 text-[13px] whitespace-pre-line text-ink-soft">{a.body}</div>}
                </div>
                <span className="shrink-0 text-[11px] text-muted" title={formatDate(a.created_at)}>
                  {formatWhen(a.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}
